import {BIService} from '../services/BIService';
import {CartStore} from '../stores/CartStore';
import {CashierExpressStore} from '../stores/CashierExpressStore';
import {CartLayout, ICartControllerApi, ICartStyleSettings, IControllerProps, VeloInputs} from '../../types/app.types';
import {NavigationStore} from '../stores/NavigationStore';
import {OrderStore} from '../stores/OrderStore';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {NavigationService} from '../services/NavigationService';
import {StyleSettingsService} from '../services/StyleSettingsService';
import {CartService} from '../services/CartService';
import {OrderService} from '../services/OrderService';
import {
  CheckoutNavigationService,
  MultilingualService,
  BaseController,
  EcomPlatformViewerScriptContext,
} from '@wix/wixstores-client-storefront-sdk';
import {EcommerceSettingsService} from '../services/EcommerceSettingsService';
import {ModalManagerService} from '../services/ModalManagerService';
import {MinimumOrderAmountService} from '../services/MinimumOrderAmountService';
import {APP_DEFINITION_ID} from '@wix/wixstores-client-core';
import {applyDefaults} from '../utils/applyDefaults';
import {SPECS} from '../specs';
import {createSlotVeloAPIFactory} from '@wix/widget-plugins-ooi/velo';
import {FedopsInteractions, SlotsMap} from '../../common/constants';
import {hackInitializeViewerScriptForPreviewBecausePreviewIsInIframe} from '../utils/hackInitializeViewerScriptForPreviewBecausePreviewIsInIframe';
import type {DeliveryOptionsService} from '@wix/delivery-section-cart/services';
import {getFormattingLanguage} from '../utils/getFormattingLanguage';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';

export abstract class BaseCartController extends BaseController {
  protected abstract getWidgetId(): string;
  protected abstract getOrigin(): string;
  protected abstract getCartLayout(): CartLayout;
  protected abstract getDefaultSettings(): ICartStyleSettings;
  protected currentCartService: EcomPlatformViewerScriptContext['currentCartService'];

  private readonly viewerContext: ViewerContext;
  private readonly services = {} as {
    biService: BIService;
    cartService: CartService;
    navigationService: NavigationService;
    modalManagerService: ModalManagerService;
    orderService: OrderService;
    styleSettingsService: StyleSettingsService;
    checkoutNavigationService: CheckoutNavigationService;
    multilingualService: MultilingualService;
    ecommerceSettingsService: EcommerceSettingsService;
    minimumOrderAmountService: MinimumOrderAmountService;
    deliveryOptionsServicePromise: Promise<DeliveryOptionsService>;
  };
  private veloInputs: VeloInputs;
  private isControllerLoaded: boolean;
  public stores = {} as {
    navigation: NavigationStore;
    cart: CartStore;
    order: OrderStore;
    cashierExpress: CashierExpressStore;
  };

  constructor(controllerParams: ControllerParams) {
    super(controllerParams);
    this.viewerContext = new ViewerContext(
      controllerParams.controllerConfig,
      controllerParams.controllerConfig.essentials.experiments.enabled(SPECS.MoveCartToViewerContext)
        ? undefined
        : this.siteStore
    );

    this.setProps({isLoading: true});
    if (this.viewerContext.isSSR()) {
      return;
    }

    this.flowAPI.fedops.interactionStarted(FedopsInteractions.lineItemsLoaded);
    this.flowAPI.fedops.interactionStarted(FedopsInteractions.padeLoaded);
    this.isControllerLoaded = false;
    this.initDefaultVeloInputs();
    this.currentCartService = (controllerParams.appData.context as EcomPlatformViewerScriptContext).currentCartService;
    this.subscribeToChanges(controllerParams.controllerConfig);
    this.setServices(controllerParams);
    if (this.viewerContext.isExperimentEnabled(SPECS.AddSlotToCartPage)) {
      const slotAPIFactory = createSlotVeloAPIFactory(controllerParams.controllerConfig);
      const slot = slotAPIFactory?.getSlotAPI(SlotsMap.mainBottom);
      slot.cartId = 'id';
    }

    this.setStores(controllerParams);
  }

  private initDefaultVeloInputs(): void {
    this.veloInputs = {shouldShowSecureCheckout: true};
  }

  public readonly updateComponent = async () => {
    this.setProps({
      isLoading: false,
      cartStore: this.stores.cart.toProps(),
      cashierExpressStore: this.stores.cashierExpress.toProps(),
      navigationStore: await this.stores.navigation.toProps(),
      orderStore: this.stores.order.toProps(),
      isResponsive: this.services.styleSettingsService.isEditorX,
      fitToContentHeight: true,
    } as IControllerProps);
  };

  public setVeloInputs = async (veloInputs: VeloInputs) => {
    this.veloInputs = {
      ...this.veloInputs,
      ...veloInputs,
    };
    this.stores.cart.setOverrides(this.veloInputs);
    if (this.isControllerLoaded) {
      await this.updateComponent();
    }
  };

  /* istanbul ignore next */
  /**
   * @deprecated
   */
  protected readonly reportFedopsInteraction = () => {
    throw new Error('Use reportFedops');
  };

  protected readonly reportFedops = async (interaction: FedopsInteractions, fn?: Function) => {
    this.flowAPI.fedops.interactionStarted(interaction);
    this.flowAPI.panoramaClient?.transaction(interaction).start();
    if (fn) {
      await fn();
    }
    this.flowAPI.fedops.interactionEnded(interaction);
    this.flowAPI.panoramaClient?.transaction(interaction).finish();
  };

  /* istanbul ignore next */
  protected readonly reportStartFedops = (interaction: FedopsInteractions) => {
    this.flowAPI.fedops.interactionStarted(interaction);
    this.flowAPI.panoramaClient?.transaction(interaction).start();
  };

  /* istanbul ignore next */
  protected readonly reportEndFedops = (interaction: FedopsInteractions) => {
    this.flowAPI.fedops.interactionEnded(interaction);
    this.flowAPI.panoramaClient?.transaction(interaction).finish();
  };

  private get controllerApi(): ICartControllerApi {
    return {
      updateComponent: this.updateComponent,
      reportFedops: this.reportFedops,
      reportStartFedops: this.reportStartFedops,
      reportEndFedops: this.reportEndFedops,
      t: this.t,
    };
  }

  public readonly load = async (): Promise<void> => {
    this.services.cartService.isSummaryUpdating = true;
    await this.updateComponent();

    await this.services.cartService.fetchCartAndEstimateTotals();
    await this.updateComponent();
  };

  public readonly init = async (): Promise<void> => {
    if (this.viewerContext.isSSR()) {
      return;
    }

    await hackInitializeViewerScriptForPreviewBecausePreviewIsInIframe(this.siteStore, this.viewerContext);

    await Promise.all([this.initFetchCartAndEstimateTotals()]);
    void this.services.ecommerceSettingsService.initPayableCurrencies();

    await this.updateComponent();
    this.isControllerLoaded = true;
  };

  private async initFetchCartAndEstimateTotals() {
    await this.services.cartService.fetchCartAndEstimateTotals(true);
    this.stores.cashierExpress.init();
  }

  public readonly onStyleUpdate = async (styleParams: {}): Promise<void> => {
    this.services.styleSettingsService.update(
      applyDefaults(this.getDefaultSettings(), styleParams as unknown as ICartStyleSettings)
    );
    await this.updateComponent();
  };

  private subscribeToChanges(controllerConfig: ControllerParams['controllerConfig']) {
    this.currentCartService.onChange(async () => {
      this.services.cartService.isSummaryUpdating = true;
      await this.controllerApi.updateComponent();
      void this.updateCurrentCart();
    });

    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
    if (moveCartToViewerContext) {
      controllerConfig.wixCodeApi.site.onInstanceChanged(
        /* istanbul ignore next */ () => void this.init(),
        APP_DEFINITION_ID
      );
    } else {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      this.siteStore.siteApis.onInstanceChanged(/* istanbul ignore next */ () => this.init(), APP_DEFINITION_ID);
    }
  }

  private async updateCurrentCart() {
    this.services.cartService.clearLoadingItems();
    await this.services.cartService.fetchCartAndEstimateTotals();
    return this.updateComponent();
  }

  private setServices(controllerParams: ControllerParams) {
    const ecommerceSettingsServiceSdk = (controllerParams.appData.context as EcomPlatformViewerScriptContext)
      .eCommerceSettingsService;
    this.services.modalManagerService = new ModalManagerService(
      {siteStore: this.siteStore, viewerContext: this.viewerContext},
      controllerParams
    );
    this.services.styleSettingsService = new StyleSettingsService(
      controllerParams,
      this.getDefaultSettings(),
      this.viewerContext
    );
    this.services.deliveryOptionsServicePromise = this.getDeliveryOptionsServicePromise(controllerParams);
    this.services.biService = new BIService({
      siteStore: this.siteStore,
      origin: this.getOrigin(),
      viewerContext: this.viewerContext,
      controllerConfig: controllerParams.controllerConfig,
    });
    this.services.ecommerceSettingsService = new EcommerceSettingsService({
      ecommerceSettingsServiceSdk,
      flowAPI: this.flowAPI,
    });
    this.services.cartService = new CartService({
      controllerApi: this.controllerApi,
      siteStore: this.siteStore,
      biService: this.services.biService,
      styleSettingsService: this.services.styleSettingsService,
      currentCartService: this.currentCartService,
      origin: this.getOrigin(),
      ecommerceSettingsService: this.services.ecommerceSettingsService,
      cartLayout: this.getCartLayout(),
      deliveryOptionsServicePromise: this.services.deliveryOptionsServicePromise,
      flowAPI: this.flowAPI,
      viewerContext: this.viewerContext,
    });
    this.services.orderService = new OrderService({
      cartService: this.services.cartService,
      ecommerceSettingsService: this.services.ecommerceSettingsService,
      styleSettingsService: this.services.styleSettingsService,
      siteStore: this.siteStore,
      viewerContext: this.viewerContext,
    });
    this.services.minimumOrderAmountService = new MinimumOrderAmountService({
      orderService: this.services.orderService,
      styleSettingsService: this.services.styleSettingsService,
    });

    const mergedPublicData = {...this.publicData.APP, ...this.publicData.COMPONENT};
    this.services.multilingualService = new MultilingualService(
      this.siteStore,
      mergedPublicData,
      {} // todo: fetch appSettings and pass here
    );
    this.services.checkoutNavigationService = new CheckoutNavigationService({
      siteStore: this.siteStore,
      origin: this.getOrigin(),
    });
    this.services.navigationService = new NavigationService({
      siteStore: this.siteStore,
      viewerContext: this.viewerContext,
      biService: this.services.biService,
      cartService: this.services.cartService,
      publicData: this.publicData,
      controllerApi: this.controllerApi,
      modalManagerService: this.services.modalManagerService,
      controllerParams,
    });
  }

  private setStores(controllerParams: ControllerParams) {
    this.stores.cart = new CartStore(
      this.flowAPI,
      this.controllerApi,
      this.viewerContext,
      this.services,
      this.veloInputs
    );
    this.stores.order = new OrderStore(this.controllerApi, this.siteStore, this.viewerContext, this.services);
    this.stores.navigation = new NavigationStore(
      this.controllerApi,
      this.siteStore,
      this.viewerContext,
      this.services,
      {
        origin: this.getOrigin(),
      }
    );
    this.stores.cashierExpress = new CashierExpressStore(
      controllerParams,
      this.controllerApi,
      this.siteStore,
      this.viewerContext,
      this.services,
      this.services.styleSettingsService,
      this.getOrigin(),
      this.getWidgetId()
    );
  }

  private async getDeliveryOptionsServicePromise(controllerParams: ControllerParams) {
    const {DeliveryOptionsService} = await import('@wix/delivery-section-cart/services');
    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);

    return new DeliveryOptionsService({
      locale: getFormattingLanguage(controllerParams.flowAPI),
      translationLang: controllerParams.flowAPI.translations.config.language,
      httpClient: controllerParams.flowAPI.httpClient,
      timezone: controllerParams.controllerConfig.wixCodeApi.site.timezone,
      experiments: moveCartToViewerContext
        ? controllerParams.controllerConfig.essentials.experiments
        : this.siteStore.experiments,
      errorHandler: controllerParams.flowAPI.errorHandler,
    });
  }
}
